import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, User, Building, CreditCard, FileText, Download, Eye, Calendar, Phone, Mail, Activity, ArrowLeft, UserX } from 'lucide-react';
import { toast } from '@etherisc/ui-kit';
import { PageContent } from '../../components/layout/PageContent';
import { NeutralButton, PositiveButton, DangerButton } from '../../components/buttons';
import { farmerService } from '../../services/farmerService';
import type { Farmer, FarmerDocument, FarmerPaymentMethod } from '../../types/farmer';
import { formatDate, formatFileSize } from '../../utils/formatters';
import { DocumentPreviewModal } from '../../components/common/DocumentPreviewModal';
import { shouldPreviewAsImage } from '../../utils/fileUtils';
import { SendEmailDialog } from '../../components/farmer/SendEmailDialog';
import { SendSmsDialog } from '../../components/farmer/SendSmsDialog';
import { usePermissions } from '../../hooks/usePermissions';
import { useAdministrativeLevelNames } from '../../hooks/useAdministrativeLevelNames';
import { getAdministrativeLevelLabels } from '../../utils/administrativeLevelLabels';
import { LocationMap } from '../../components/quote/components/LocationMap';

interface BankTransferPayment extends FarmerPaymentMethod {
  type: 'bank_transfer';
  account_name: string;
  bank_name: string;
  account_number: string;
  currency: string;
  branch_name_or_code?: string;
}

interface MobileMoneyPayment extends FarmerPaymentMethod {
  type: 'mobile_money';
  registered_mobile_number: string;
  full_name: string;
  provider?: string;
}

const isBankTransfer = (payment: FarmerPaymentMethod): payment is BankTransferPayment => {
  return payment.type === 'bank_transfer';
};

const isMobileMoney = (payment: FarmerPaymentMethod): payment is MobileMoneyPayment => {
  return payment.type === 'mobile_money';
};

export function FarmerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [farmer, setFarmer] = useState<Farmer | null>(null);
  const [loading, setLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [imagePreview, setImagePreview] = useState<{
    isOpen: boolean;
    document: FarmerDocument | null;
  }>({ isOpen: false, document: null });
  
  // Unwrap administrative levels for the hook
  const unwrappedLevels = farmer?.administrativeLevels && typeof farmer.administrativeLevels === 'object' && 'data' in farmer.administrativeLevels
    ? (farmer.administrativeLevels as Record<string, unknown>).data as Record<string, string>
    : farmer?.administrativeLevels;

  // Resolve administrative level codes to names
  const { names: levelNames, loading: levelsLoading } = useAdministrativeLevelNames(
    unwrappedLevels,
    farmer?.countryCode || 'ZW'
  );

  // Unwrap farmer location administrative levels
  const unwrappedFarmerLevels = farmer?.farmerLocation?.administrativeLevels && 
    typeof farmer.farmerLocation.administrativeLevels === 'object' && 
    'data' in farmer.farmerLocation.administrativeLevels
    ? (farmer.farmerLocation.administrativeLevels as Record<string, unknown>).data as Record<string, string>
    : farmer?.farmerLocation?.administrativeLevels;

  // Resolve farmer location codes to names
  const { names: farmerLevelNames, loading: farmerLevelsLoading } = useAdministrativeLevelNames(
    unwrappedFarmerLevels,
    farmer?.countryCode || 'ZW'
  );

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);

  useEffect(() => {
    if (id) {
      loadFarmer(id);
    }
  }, [id]);

  const loadFarmer = async (farmerId: string) => {
    try {
      setLoading(true);
      const response = await farmerService.getFarmerById(farmerId);
      
      // Validate the farmer data structure
      if (!response.farmer) {
        throw new Error('No farmer data received');
      }
      
      // Keep schema-wrapped data as-is (don't convert to empty arrays)
      // Schema-wrapped format: { data: [...], schemaId: '...', schemaVersion: '...' }
      const farmerData = {
        ...response.farmer
      };
      
      setFarmer(farmerData);
    } catch (error) {
      console.error('Error loading farmer:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load farmer details';
      toast.error(`Error: ${errorMessage}`);
      navigate('/admin/farmers');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!farmer) return;
    
    setIsGeneratingPDF(true);
    try {
      const response = await fetch(`/api/documents/farmer/${farmer.id}/profile-pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth-token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to generate farmer profile PDF: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success && result.data?.pdfUrl) {
        // Open the PDF in a new tab with authentication
        const token = localStorage.getItem('auth-token');
        
        // Fetch PDF with authentication and create blob URL
        const pdfResponse = await fetch(result.data.pdfUrl, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
        
        if (pdfResponse.ok) {
          const blob = await pdfResponse.blob();
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
          
          // Clean up blob URL after delay
          setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
        }
        
        toast.success('Farmer profile PDF has been generated successfully.');
      } else {
        throw new Error(result.message || 'Failed to generate farmer profile PDF');
      }
    } catch (error) {
      console.error('Error generating farmer profile PDF:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate farmer profile PDF';
      toast.error(errorMessage);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/farmers');
  };

  const handleEdit = () => {
    if (farmer) {
      navigate(`/admin/farmers/${farmer.id}/edit`);
    }
  };

  const handleDeactivate = async () => {
    if (!farmer) return;
    if (!confirm(`Are you sure you want to deactivate farmer "${farmer.name}"?\n\nThis will make the farmer inactive but preserve all data.`)) {
      return;
    }
    try {
      await farmerService.deleteFarmer(farmer.id);
      toast.success(`Farmer "${farmer.name}" has been deactivated successfully.`);
      navigate('/admin/farmers');
    } catch (err) {
      console.error('Error deactivating farmer:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to deactivate farmer.';
      toast.error(errorMessage);
    }
  };

  const viewDocument = (document: FarmerDocument) => {
    // Check if it's an image or PDF that should be previewed in modal
    const isPDF = document.mimeType === 'application/pdf' || document.filename.toLowerCase().endsWith('.pdf');
    
    if (shouldPreviewAsImage(document) || isPDF) {
      setImagePreview({ isOpen: true, document });
    } else {
      // Open other document types in new tab/window
      window.open(document.url, '_blank');
    }
  };

  const renderAdministrativeLevels = () => {
    if (!unwrappedLevels || typeof unwrappedLevels !== 'object') {
      return <p className="text-gray-500">No administrative levels recorded</p>;
    }
    
    if (levelsLoading) {
      return <p className="text-gray-500 italic">Loading location names...</p>;
    }
    
    try {
      const levels = Object.entries(unwrappedLevels).sort(([a], [b]) => parseInt(a) - parseInt(b));
      
      if (levels.length === 0) {
        return <p className="text-gray-500">No administrative levels recorded</p>;
      }
      
      // Get level labels from centralized config
      const levelLabels = getAdministrativeLevelLabels(farmer?.countryCode || 'ZW');
      
      return (
        <div className="space-y-2">
          {levels.map(([level, code]) => (
            <div key={level} className="flex justify-between">
              <span className="text-gray-600">{levelLabels[level] || `Level ${level}`}:</span>
              <span className="font-medium">{levelNames[level] || code}</span>
            </div>
          ))}
        </div>
      );
    } catch (error) {
      console.error('Error rendering administrative levels:', error);
      // Fallback: Just show as string
      return <span className="font-medium">{String(farmer?.administrativeLevels) || 'Not specified'}</span>;
    }
  };

  const renderPaymentMethods = () => {
    // Unwrap schema-wrapped data if needed
    const unwrappedPayments = farmer?.paymentDetails && typeof farmer.paymentDetails === 'object' && 'data' in farmer.paymentDetails
      ? (farmer.paymentDetails as Record<string, unknown>).data as FarmerPaymentMethod[]
      : farmer?.paymentDetails;

    if (!unwrappedPayments || !Array.isArray(unwrappedPayments) || unwrappedPayments.length === 0) {
      return <p className="text-gray-500 italic">No payment methods configured</p>;
    }

    return (
      <div className="space-y-4">
        {unwrappedPayments.map((payment, index) => (
          <div key={index} className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">
              Payment Method {index + 1} - {payment.type === 'bank_transfer' ? 'Bank Transfer' : 'Mobile Money'}
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {isBankTransfer(payment) && (
                <>
                  <div><span className="text-gray-600">Account Name:</span> {payment.account_name}</div>
                  <div><span className="text-gray-600">Bank:</span> {payment.bank_name}</div>
                  <div><span className="text-gray-600">Account Number:</span> {payment.account_number}</div>
                  <div><span className="text-gray-600">Currency:</span> {payment.currency}</div>
                  {payment.branch_name_or_code && (
                    <div><span className="text-gray-600">Branch:</span> {payment.branch_name_or_code}</div>
                  )}
                </>
              )}
              {isMobileMoney(payment) && (
                <>
                  <div><span className="text-gray-600">Mobile Number:</span> {payment.registered_mobile_number}</div>
                  <div><span className="text-gray-600">Full Name:</span> {payment.full_name}</div>
                  {payment.provider && (
                    <div><span className="text-gray-600">Provider:</span> {payment.provider}</div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderDocuments = (documents: FarmerDocument[] | null | undefined, title: string) => {
    // Unwrap schema-wrapped data if needed
    const unwrappedDocs = documents && typeof documents === 'object' && 'data' in documents
      ? (documents as Record<string, unknown>).data as FarmerDocument[]
      : documents;
    
    // Filter out placeholder documents (id starts with all zeros)
    const realDocuments = unwrappedDocs?.filter((doc: FarmerDocument) => 
      doc && doc.id && !doc.id.startsWith('00000000-0000-0000')
    ) || [];

    if (!unwrappedDocs || !Array.isArray(unwrappedDocs) || realDocuments.length === 0) {
      return (
        <div>
          <h4 className="font-medium text-gray-900 mb-2">{title}</h4>
          <p className="text-gray-500 italic">No documents uploaded</p>
        </div>
      );
    }

    try {
      return (
        <div>
          <h4 className="font-medium text-gray-900 mb-3">{title}</h4>
          <div className="space-y-3">
            {realDocuments.map((doc, index) => (
              <div key={doc?.id || index} className="bg-gray-50 p-3 rounded-lg flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-gray-900">{doc?.filename || 'Unknown file'}</p>
                    <p className="text-sm text-gray-600">
                      {doc?.size ? formatFileSize(doc.size) : 'Unknown size'} • {doc?.mimeType || 'Unknown type'} • {doc?.uploadedAt ? formatDate(doc.uploadedAt) : 'Unknown date'}
                    </p>
                  </div>
                </div>
                {doc?.url && (
                  <button
                    onClick={() => viewDocument(doc)}
                    className="text-blue-600 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-50"
                    title="View document"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    } catch (error) {
      console.error('Error rendering documents:', error);
      return (
        <div>
          <h4 className="font-medium text-gray-900 mb-2">{title}</h4>
          <p className="text-gray-500 italic">Error displaying documents</p>
        </div>
      );
    }
  };

  const renderMap = () => {
    if (!farmer?.latitude || !farmer?.longitude) {
      return (
        <div className="bg-gray-100 rounded-lg p-8 text-center">
          <MapPin className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">Location coordinates not available</p>
        </div>
      );
    }

    // Use live OSM map
    const locations = [{
      id: farmer.id,
      name: farmer.name,
      lat: Number(farmer.latitude),
      lng: Number(farmer.longitude),
      status: 'valid' as const,
      farmer: {
        name: farmer.name,
        phone: farmer.phone,
        area: farmer.area?.toString()
      }
    }];

    return (
      <div>
        <LocationMap 
          locations={locations} 
          height="350px" 
          center={{ lat: Number(farmer.latitude), lng: Number(farmer.longitude) }}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <PageContent title="Loading..." description="Loading farmer details...">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </PageContent>
    );
  }

  if (!farmer) {
    return (
      <PageContent title="Farmer Not Found" description="The requested farmer could not be found.">
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">The farmer you're looking for doesn't exist or you don't have permission to view it.</p>
          <NeutralButton onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Farmers
          </NeutralButton>
        </div>
      </PageContent>
    );
  }

  // Additional safety check for farmer data integrity
  if (!farmer.id || !farmer.name) {
    return (
      <PageContent title="Invalid Farmer Data" description="The farmer data appears to be corrupted.">
        <div className="text-center py-12">
          <User className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Invalid farmer data</h3>
          <p className="text-gray-600 mb-6">The farmer data is corrupted or incomplete. Please contact support.</p>
          <NeutralButton onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Farmers
          </NeutralButton>
        </div>
      </PageContent>
    );
  }

  const pageActions = (
    <div className="flex items-center space-x-3">
      <NeutralButton onClick={handleBack}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Farmers
      </NeutralButton>
      <NeutralButton onClick={handleEdit}>
        Edit Farmer
      </NeutralButton>
      {hasPermission('farmers:delete') && farmer.isActive && (
        <DangerButton onClick={handleDeactivate} showIcon={false}>
          <UserX className="w-4 h-4" />
          Deactivate
        </DangerButton>
      )}
      <NeutralButton onClick={() => setEmailDialogOpen(true)}>
        <Mail className="w-4 h-4 mr-2" />
        Send Email
      </NeutralButton>
      {hasPermission('notifications:send') && (
        <NeutralButton 
          onClick={() => setSmsDialogOpen(true)}
          disabled={!farmer.phone}
        >
          <Phone className="w-4 h-4 mr-2" />
          Send SMS
        </NeutralButton>
      )}
      <PositiveButton 
        onClick={handleGeneratePDF}
        disabled={isGeneratingPDF}
      >
        <Download className="w-4 h-4 mr-2" />
        {isGeneratingPDF ? 'Generating...' : 'Generate PDF'}
      </PositiveButton>
    </div>
  );

  return (
    <PageContent 
      title={farmer.name}
      description={`Farmer Code: ${farmer.farmerCode}`}
      actions={pageActions}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Personal Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2" />
              Personal Information
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Name:</span>
                <span className="font-medium">{farmer.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Phone:</span>
                <span className="font-medium flex items-center">
                  <Phone className="w-4 h-4 mr-1" />
                  {farmer.phone}
                </span>
              </div>
              {farmer.email && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium flex items-center">
                    <Mail className="w-4 h-4 mr-1" />
                    {farmer.email}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Registered:</span>
                <span className="font-medium flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {formatDate(farmer.createdAt)}
                </span>
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPin className="w-5 h-5 mr-2" />
              Location Information
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Country:</span>
                <span className="font-medium">{farmer.countryCode}</span>
              </div>
              {farmer.administrativeLevels && (
                <div>
                  <p className="text-gray-600 mb-2">Administrative Levels:</p>
                  {renderAdministrativeLevels()}
                </div>
              )}
              {(farmer.latitude != null && farmer.longitude != null) ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Latitude:</span>
                    <span className="font-medium">{Number(farmer.latitude).toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Longitude:</span>
                    <span className="font-medium">{Number(farmer.longitude).toFixed(4)}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between">
                  <span className="text-gray-600">Coordinates:</span>
                  <span className="text-gray-400 italic">Not available</span>
                </div>
              )}
            </div>
          </div>

          {/* Farmer Residential Address (if separate) */}
          {farmer.hasSeparateFarmerLocation && farmer.farmerLocation && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                Farmer Residential Address
              </h3>
              <div className="space-y-3">
                {unwrappedFarmerLevels && (
                  <div>
                    <p className="text-gray-600 mb-2">Administrative Levels:</p>
                    {farmerLevelsLoading ? (
                      <p className="text-gray-500 italic">Loading location names...</p>
                    ) : (
                      <div className="space-y-2">
                        {Object.entries(unwrappedFarmerLevels)
                          .sort(([a], [b]) => parseInt(a) - parseInt(b))
                          .map(([level, code]) => {
                            const label = getAdministrativeLevelLabels(farmer.countryCode)[level] || `Level ${level}`;
                            return (
                              <div key={level} className="flex justify-between">
                                <span className="text-gray-600">{label}:</span>
                                <span className="font-medium">{farmerLevelNames[level] || code}</span>
                              </div>
                            );
                          })
                        }
                      </div>
                    )}
                  </div>
                )}
                {farmer.farmerLocation.village && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Village:</span>
                    <span className="font-medium">{farmer.farmerLocation.village}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Agricultural Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Activity className="w-5 h-5 mr-2" />
              Agricultural Information
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Primary Activity:</span>
                <span className="font-medium">{farmer.primaryActivity || farmer.crop || 'Not specified'}</span>
              </div>
              {farmer.otherActivities && Array.isArray(farmer.otherActivities) && farmer.otherActivities.length > 0 && (
                <div>
                  <span className="text-gray-600">Other Activities:</span>
                  <div className="mt-1">
                    {farmer.otherActivities.map((activity, index) => (
                      <span key={index} className="inline-block bg-blue-100 text-blue-800 text-xs font-medium mr-2 mb-1 px-2.5 py-0.5 rounded">
                        {activity || 'Unknown activity'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {farmer.area && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Farm Size:</span>
                  <span className="font-medium">{farmer.area} hectares</span>
                </div>
              )}
              {farmer.herdSize !== undefined && farmer.herdSize !== null && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Herd Size:</span>
                  <span className="font-medium">{farmer.herdSize}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Map */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPin className="w-5 h-5 mr-2" />
              Farm Location
            </h3>
            {renderMap()}
          </div>

          {/* Organization Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Building className="w-5 h-5 mr-2" />
              Organization
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-gray-600">Primary Organization:</p>
                <p className="font-medium">
                  {farmer.organization
                    ? `${farmer.organization.name} (${farmer.organization.code})`
                    : 'Not assigned'}
                </p>
              </div>
              {(() => {
                const raw: unknown = farmer.organizationIds;
                const orgIds = raw && typeof raw === 'object' && !Array.isArray(raw) && 'data' in (raw as Record<string, unknown>)
                  ? (raw as Record<string, unknown>).data as string[]
                  : raw as string[] | null | undefined;
                if (!orgIds || !Array.isArray(orgIds) || orgIds.length === 0) return null;
                return (
                  <div>
                    <p className="text-gray-600">Additional Organizations:</p>
                    <p className="text-sm text-gray-500">{orgIds.length} additional organization(s)</p>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Payment Methods */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CreditCard className="w-5 h-5 mr-2" />
              Payment Methods
            </h3>
            {renderPaymentMethods()}
          </div>
        </div>
      </div>

      {/* Documents Section - Full Width - Only show if there are real documents */}
      {(() => {
        // Unwrap schema-wrapped data if needed
        const unwrappedDocs = farmer.proofOfStake && typeof farmer.proofOfStake === 'object' && 'data' in farmer.proofOfStake
          ? (farmer.proofOfStake as Record<string, unknown>).data as FarmerDocument[]
          : farmer.proofOfStake;
        
        const realDocs = unwrappedDocs?.filter((doc: FarmerDocument) => 
          doc && doc.id && !doc.id.startsWith('00000000-0000-0000')
        ) || [];
        
        if (realDocs.length === 0) return null;
        
        return (
          <div className="mt-8 space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Documents
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {renderDocuments(farmer.proofOfStake, "Proof of Stake/Insurable Interest")}
                {/* Add national ID documents if they exist in the farmer object */}
                {/* {renderDocuments(farmer.nationalIdDocuments, "National ID Documents")} */}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Document Preview Modal */}
      {imagePreview.document && (
        <DocumentPreviewModal
          isOpen={imagePreview.isOpen}
          onClose={() => setImagePreview({ isOpen: false, document: null })}
          documentUrl={imagePreview.document.url}
          fileName={imagePreview.document.filename}
          fileSize={imagePreview.document.size}
          mimeType={imagePreview.document.mimeType}
        />
      )}

      {/* Email Dialog */}
      {farmer && (
        <SendEmailDialog
          isOpen={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          farmer={farmer}
        />
      )}

      {/* SMS Dialog */}
      {farmer && (
        <SendSmsDialog
          isOpen={smsDialogOpen}
          onClose={() => setSmsDialogOpen(false)}
          farmer={farmer}
        />
      )}
    </PageContent>
  );
}
